import * as React from 'react';
import { Modal, Button, Tabs, Tab, Form, Table, Alert, Row, Col, Accordion, Badge } from 'react-bootstrap';
import { LuShield, LuLock, LuCheck, LuInfo, LuChevronDown } from 'react-icons/lu';

export interface SecurityOption {
    id: string | number;
    name: string;             
    grantedPermissionIds?: (string|number)[]; 
}

interface SecurityModalProps {
    show: boolean;
    onHide: () => void;
    onApply: (selectedRoles: string[], selectedPermissions: string[]) => void; 
    userEmail: string;
    availableRoles: SecurityOption[];
    availablePermissions: SecurityOption[];
    initialRoles: string[];
    initialPermissions: string[];
    showRolesTab?: boolean; 
}

export const UserSecurityModal: React.FC<SecurityModalProps> = ({ 
    show, onHide, onApply, userEmail, 
    availableRoles, availablePermissions, 
    initialRoles, initialPermissions,
    showRolesTab = true 
}) => {
    
    const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);
    const [selectedPermissions, setSelectedPermissions] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (show) {
            setSelectedRoles(initialRoles || []);
            setSelectedPermissions(initialPermissions || []);
        }
    }, [show, initialRoles, initialPermissions]);

    const toggleRole = (id: string) => {
        if (selectedRoles.includes(id)) {
            setSelectedRoles(selectedRoles.filter(r => r !== id));
        } else {
            setSelectedRoles([...selectedRoles, id]);
        }
    };

    const togglePermission = (id: string) => {
        if (selectedPermissions.includes(id)) {
            setSelectedPermissions(selectedPermissions.filter(p => p !== id));
        } else {
            setSelectedPermissions([...selectedPermissions, id]);
        }
    };

    // --- LOGIC: Calculate Implicit vs Explicit Permissions ---

    // 1. Identify which permissions are covered by the selected roles
    const getRoleGrantMap = () => {
        const map = new Map<string, string[]>();
        selectedRoles.forEach(rId => {
            const role = availableRoles.find(r => String(r.id) === rId);
            if (role && role.grantedPermissionIds) {
                role.grantedPermissionIds.forEach(pId => {
                    const pIdStr = String(pId);
                    const current = map.get(pIdStr) || [];
                    current.push(role.name);
                    map.set(pIdStr, current);
                });
            }
        });
        return map;
    };

    const grantMap = getRoleGrantMap();

    // 2. Filter: Permissions available to assign (Not covered by a role)
    //    Sorted Alphabetically
    const assignablePermissions = availablePermissions
        .filter(p => !grantMap.has(String(p.id)))
        .sort((a, b) => a.name.localeCompare(b.name));

    // 3. Filter: Permissions currently inherited (Covered by a role)
    //    We need to map the Role -> Permissions for the Accordion
    const getInheritedBreakdown = () => {
        return selectedRoles.map(rId => {
            const role = availableRoles.find(r => String(r.id) === rId);
            if (!role) return null;

            // Get permissions specific to this role
            const rolePerms = (role.grantedPermissionIds || [])
                .map(pId => availablePermissions.find(ap => String(ap.id) === String(pId)))
                .filter(Boolean) as SecurityOption[]; // Filter out bad IDs
            
            // Sort them A-Z
            rolePerms.sort((a, b) => a.name.localeCompare(b.name));

            return { roleName: role.name, permissions: rolePerms };
        }).filter(Boolean) as { roleName: string, permissions: SecurityOption[] }[];
    };

    const inheritedRoles = getInheritedBreakdown();
    const totalInheritedCount = inheritedRoles.reduce((sum, r) => sum + r.permissions.length, 0);

    return (
        <Modal show={show} onHide={onHide} size="xl" scrollable backdrop="static">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="d-flex align-items-center">
                    <LuShield className="me-2 text-primary" />
                    <span className="fw-bold">Security Settings:</span> 
                    <span className="text-muted ms-2 fs-6 font-monospace">{userEmail}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <Tabs 
                    defaultActiveKey={showRolesTab ? "roles" : "permissions"} 
                    id="security-tabs" 
                    className="px-3 pt-3 border-bottom-0"
                >
                    
                    {/* --- TAB 1: ROLES (CONDITIONAL) --- */}
                    {showRolesTab && (
                        <Tab eventKey="roles" title={`Roles (${selectedRoles.length})`}>
                            <div className="p-3 bg-white">
                                <Alert variant="info" className="small mb-3">
                                    <LuInfo className="me-2 inline-block"/>
                                    <strong>Role Assignment:</strong> Changes here will be saved when you submit the Contact.
                                </Alert>
                                <Row>
                                    {availableRoles.map(role => {
                                        const idStr = String(role.id);
                                        const isSelected = selectedRoles.includes(idStr);
                                        return (
                                            <Col md={4} sm={6} key={idStr} className="mb-3">
                                                <div 
                                                    className={`p-3 border rounded h-100 cursor-pointer ${isSelected ? 'bg-primary-subtle border-primary' : 'bg-light hover-bg-gray-100'}`}
                                                    onClick={() => toggleRole(idStr)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <Form.Check 
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => {}} 
                                                            label={<span className="fw-bold">{role.name}</span>}
                                                            style={{ pointerEvents: 'none' }}
                                                        />
                                                        {isSelected && <LuCheck className="text-primary" />}
                                                    </div>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        </Tab>
                    )}

                    {/* --- TAB 2: PERMISSIONS --- */}
                    <Tab eventKey="permissions" title="Permissions">
                        <div className="p-3 bg-white">
                            
                            {/* SECTION A: Inherited Permissions (Accordion) */}
                            {inheritedRoles.length > 0 && (
                                <Accordion className="mb-4 shadow-sm" defaultActiveKey="0">
                                    <Accordion.Item eventKey="0" className="border-0">
                                        <Accordion.Header className="bg-light">
                                            <div className="d-flex align-items-center w-100">
                                                <LuLock className="me-2 text-muted"/> 
                                                <span className="fw-bold text-muted me-2">Inherited Permissions</span>
                                                <Badge bg="secondary" pill>{totalInheritedCount} granted via Roles</Badge>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-light pt-0">
                                            <div className="pt-2">
                                                {inheritedRoles.map((role, idx) => (
                                                    <div key={idx} className="mb-3">
                                                        <h6 className="text-primary fw-bold border-bottom pb-1 mb-2 small">{role.roleName}</h6>
                                                        <Row>
                                                            {role.permissions.map(p => (
                                                                <Col md={4} key={p.id} className="mb-1 d-flex align-items-center text-muted small">
                                                                    <LuCheck className="me-1 text-success" size={14} /> {p.name}
                                                                </Col>
                                                            ))}
                                                            {role.permissions.length === 0 && <Col className="text-muted small">No specific permissions mapped.</Col>}
                                                        </Row>
                                                    </div>
                                                ))}
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            )}

                            {/* SECTION B: Assignable Permissions (Table) */}
                            <div className="border rounded">
                                <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                                    <span className="fw-bold small text-uppercase">Direct Assignments</span>
                                    <span className="small text-muted">Select additional permissions not covered by roles.</span>
                                </div>
                                
                                {assignablePermissions.length > 0 ? (
                                    <Table striped hover size="sm" className="mb-0">
                                        <tbody>
                                            {assignablePermissions.map(perm => {
                                                const idStr = String(perm.id);
                                                const isExplicit = selectedPermissions.includes(idStr);

                                                return (
                                                    <tr key={idStr} className={isExplicit ? 'bg-primary-subtle' : ''}>
                                                        <td className="align-middle">
                                                            {perm.name}
                                                        </td>
                                                        <td className="text-center align-middle" style={{width: '120px'}}>
                                                            <Form.Check 
                                                                type="checkbox"
                                                                checked={isExplicit}
                                                                onChange={() => togglePermission(idStr)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <div className="p-4 text-center text-muted">
                                        <LuCheck className="mb-2 text-success" size={24} />
                                        <p className="mb-0">All available permissions are currently covered by the selected roles.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={() => onApply(selectedRoles, selectedPermissions)}>
                    Apply Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};